/* The four shapes the whole catalogue is built from: a tile, a row of tiles,
   a billboard and a trail of crumbs.
   OWNED BY ITEM 4 OF docs/plan/00-prestavba-2026-08.md. */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../lib/i18n";
import type { Localized } from "../../lib/types";

/* Cover art is generated SVG computed from the item's own id (lib/covers.ts).
   It carries no text and nothing a user typed, so there is nothing here for
   markup to escape. */
function Art({ svg, className }: { svg: string; className?: string }) {
  return <span className={className ?? "tile-art"} dangerouslySetInnerHTML={{ __html: svg }} />;
}

export function Tile({ to, art, title, sub, foot, wide }: {
  to: string; art: string; title: Localized; sub?: Localized;
  foot?: ReactNode; wide?: boolean;
}) {
  const { t } = useI18n();
  return (
    <Link className={"tile" + (wide ? " wide" : "")} to={to}>
      <Art svg={art} />
      <span className="tile-body">
        <span className="tile-title">{t(title)}</span>
        {sub ? <span className="tile-sub">{t(sub)}</span> : null}
        {foot ? <span className="tile-foot">{foot}</span> : null}
      </span>
    </Link>
  );
}

/* A row of tiles that scrolls sideways, with arrows that appear only when there
   is somewhere to go. A dead arrow is worse than none: it says the row is
   broken rather than that it is short. */
export function Row({ title, count, children }: {
  title: ReactNode; count?: ReactNode; children: ReactNode;
}) {
  const { ui } = useI18n();
  const strip = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = strip.current;
    if (!el) return;
    setEdges({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    measure();
    const el = strip.current;
    if (!el) return;
    // The tiles are laid out by the fonts, so the first measurement can be
    // taken before they land; an observer is cheaper than guessing a delay.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  function nudge(direction: -1 | 1) {
    const el = strip.current;
    if (!el) return;
    // Not a whole screen: leaving a tile visible on the far edge is what tells
    // the reader the row continued rather than restarted.
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: "smooth" });
  }

  return (
    <section className="cat-row">
      <div className="cat-row-head">
        <h2 className="cat-row-title">{title}</h2>
        {count ? <span className="cat-row-count">{count}</span> : null}
        <span className="cat-row-arrows">
          <button type="button" className="cat-arrow" disabled={!edges.left}
                  aria-label={ui("row.back")} onClick={() => nudge(-1)}>‹</button>
          <button type="button" className="cat-arrow" disabled={!edges.right}
                  aria-label={ui("row.forward")} onClick={() => nudge(1)}>›</button>
        </span>
      </div>
      <div className="cat-strip" ref={strip} onScroll={measure}>{children}</div>
    </section>
  );
}

/* A plain grid, for a leaf listing where a row that scrolls would hide half of
   what the reader came for. */
export function Grid({ children }: { children: ReactNode }) {
  return <div className="cat-grid">{children}</div>;
}

/* The billboard: cover art as a wide backdrop with the text laid over it.
   The art is sliced rather than fitted, so a 3:2 composition becomes a banner
   without being squashed. */
export function Billboard({ art, eyebrow, title, lead, meta }: {
  art?: string; eyebrow?: ReactNode; title: ReactNode; lead?: ReactNode; meta?: ReactNode;
}) {
  return (
    <header className={"cat-bill" + (art ? " has-art" : "")}>
      {art ? <Art svg={art} className="cat-bill-art" /> : null}
      <div className="cat-bill-in">
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h1>{title}</h1>
        {lead ? <p className="lead">{lead}</p> : null}
        {meta ? <div className="cat-meta">{meta}</div> : null}
      </div>
    </header>
  );
}

export function Chip({ children }: { children: ReactNode }) {
  return <span className="cat-chip">{children}</span>;
}

export function Crumbs({ parts }: { parts: { label: ReactNode; to?: string }[] }) {
  return (
    <nav className="crumbs" aria-label="crumbs">
      {parts.map((p, i) => (
        <span key={i}>
          {i ? <span className="sep">/</span> : null}
          {p.to ? <Link to={p.to}>{p.label}</Link> : <span aria-current="page">{p.label}</span>}
        </span>
      ))}
    </nav>
  );
}
